/**
 *
 * HomeHeroCarousel
 *
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classnames from 'classnames';
import Carousel from 'react-multi-carousel';
import { makeStyles, useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Mixpanel from 'utils/mixpanelService';
import 'react-multi-carousel/lib/styles.css';
import { makeSelectShowOnboardingQuestions } from 'containers/Main/selectors';
import { useSelector } from 'react-redux';
import PauseButton from './components/PauseButton';
import ArrowLeft from './images/baseline-chevron_left-24px.svg';
import ArrowRight from './images/baseline-chevron_right-24px.svg';
import useIsOnScreen from './hooks/useIsOnScreen';
import useOverlayDetection from './hooks/useOverlayDetection';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
  },
  carouselRoot: {
    position: 'relative',
    width: '100%',
    '& > ul > li > div > div': {
      [theme.breakpoints.up('sm')]: {
        padding: '0 84px',
      },
      [theme.breakpoints.up('1140')]: {
        padding: '0 72px',
      },
      [theme.breakpoints.up('1160')]: {
        padding: '0 64px',
      },
      [theme.breakpoints.up('1180')]: {
        padding: '0 56px',
      },
      [theme.breakpoints.up('1200')]: {
        padding: '0 44px',
      },
      [theme.breakpoints.up('1220')]: {
        padding: '0 36px',
      },
      [theme.breakpoints.up('1240')]: {
        padding: '0 28px',
      },
      [theme.breakpoints.up('1260')]: {
        padding: '0 20px',
      },
      [theme.breakpoints.up('1300')]: {
        padding: 0,
      },
    },
    '& > ul > li > div > div > div > div': {
      [theme.breakpoints.up('sm')]: {
        padding: '24px 0',
      },
      [theme.breakpoints.up('1140')]: {
        width: '100%',
      },
      [theme.breakpoints.up('1300')]: {
        maxWidth: '100%',
        width: 1140,
      },
    },
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    height: 51,
    width: 51,
    borderRadius: '50%',
    zIndex: 1,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: '#FFFFFF',
    overflow: 'hidden',
  },
  leftArrow: {
    left: 28,
    [theme.breakpoints.between('1140', '1300')]: {
      left: 16,
    },
  },
  rightArrow: {
    right: 28,
    [theme.breakpoints.between('1140', '1300')]: {
      right: 16,
    },
  },
  dotsWrapper: {
    left: '50% !important',
    transform: 'translateX(-50%)',
    bottom: '16px !important',
    [theme.breakpoints.down('xs')]: {
      top: '20px !important',
      bottom: 'auto !important',
    },
  },
  dot: {
    height: 10,
    width: 10,
    borderRadius: 5,
    borderWidth: 0,
    background: '#0000000A',
    marginLeft: 4,
    marginRight: 4,
    padding: 0,
    cursor: 'pointer',
  },
  activeDot: {
    background: '#FFFFFF',
    border: '1px solid #F0F0F0',
  },
}));

const CustomLeftArrow = ({ className, onClick }) => (
  <button type="button" onClick={onClick} className={className}>
    <img src={ArrowLeft} alt="Arrow Left" />
  </button>
);
const CustomRightArrow = ({ className, onClick }) => (
  <button type="button" onClick={onClick} className={className}>
    <img src={ArrowRight} alt="Arrow Right" />
  </button>
);

const CustomDot = ({ classes, active, index, onClick, onDotClick }) => {
  const handleClick = () => {
    onClick();
    onDotClick();
  };

  return (
    <button
      type="button"
      aria-label={index}
      onClick={handleClick}
      className={_classnames({
        [classes.dot]: true,
        [classes.active]: active,
      })}
    />
  );
};

function HomeHeroCarousel({ children, timer = 8000 }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [pausedByUser, setPausedByUser] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const ref = useRef(null);
  const onScreen = useIsOnScreen(ref, {
    isCarousel: true,
    threshold: 0.7,
  });
  const overlayDetected = useOverlayDetection();
  const timeout = useRef();
  const interval = useRef();
  const startTime = useRef(Date.now());
  const remainingTime = useRef();
  const openOnboarding = useSelector(makeSelectShowOnboardingQuestions());

  useEffect(() => {
    setIsPlaying(
      !openOnboarding && !pausedByUser && onScreen && !overlayDetected,
    );
  }, [onScreen, overlayDetected, pausedByUser, openOnboarding]);

  useEffect(() => {
    if (isPlaying) {
      clearTimeout(timeout.current);
      clearInterval(interval.current);
      interval.current = setInterval(toNextSlide, timer);
      startTime.current = Date.now();
    }

    return () => {
      clearTimeout(timeout.current);
      clearInterval(interval.current);
      interval.current = undefined;
    };
  }, [isPlaying, timer, toNextSlide]);

  useEffect(() => {
    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        if (remainingTime.current)
          timeout.current = setTimeout(() => {
            toNextSlide();
            if (!interval.current)
              interval.current = setInterval(toNextSlide, timer);
          }, remainingTime.current);
      } else {
        clearTimeout(timeout.current);
        clearInterval(interval.current);
        interval.current = undefined;
        remainingTime.current = timer - (Date.now() - startTime.current);
      }
    };
    if (isPlaying)
      document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () =>
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
  }, [isPlaying, timer, toNextSlide]);

  const resetInterval = () => {
    if (isPlaying) {
      clearTimeout(timeout.current);
      clearInterval(interval.current);
      interval.current = setInterval(toNextSlide, timer);
    }
  };

  const toNextSlide = useCallback(() => {
    ref.current.next();
    if (isPlaying) startTime.current = Date.now();
  }, [isPlaying]);

  const onLeftClick = () => {
    ref.current.previous();
    if (isPlaying) startTime.current = Date.now();
  };

  const onRightClick = () => {
    Mixpanel.track('Home Hero - Carousel Right Arrow - Clicked');
    toNextSlide();
  };

  const onDotClick = () => {
    Mixpanel.track('Home Hero - Carousel Dot - Clicked');
    if (isPlaying) startTime.current = Date.now();
  };

  const onMouseEnterHandler = () => {
    if (isPlaying) {
      clearTimeout(timeout.current);
      clearInterval(interval.current);
      interval.current = undefined;
      remainingTime.current = timer - (Date.now() - startTime.current);
    }
  };

  const onMouseLeaveHandler = () => {
    if (isPlaying && remainingTime.current)
      timeout.current = setTimeout(() => {
        toNextSlide();
        if (!interval.current)
          interval.current = setInterval(toNextSlide, timer);
      }, remainingTime.current);
  };

  const onPauseClick = () => {
    setPausedByUser(!pausedByUser);
  };

  return (
    <Box
      className={classes.container}
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
    >
      <Carousel
        ref={ref}
        arrows={false}
        containerClass={classes.carouselRoot}
        dotListClass={classes.dotsWrapper}
        draggable={false}
        beforeChange={resetInterval}
        infinite
        showDots
        customDot={
          <CustomDot
            classes={{ dot: classes.dot, active: classes.activeDot }}
            onDotClick={onDotClick}
          />
        }
        customTransition="transform 800ms ease-in-out"
        transitionDuration={800}
        shouldResetAutoplay
        focusOnSelect={false}
        keyBoardControl
        minimumTouchDrag={80}
        slidesToSlide={1}
        swipeable
        responsive={{
          all: {
            breakpoint: {
              max: 4000,
              min: 0,
            },
            items: 1,
          },
        }}
      >
        {children}
      </Carousel>
      {!isMobile && (
        <>
          <CustomLeftArrow
            className={_classnames(classes.arrow, classes.leftArrow)}
            onClick={onLeftClick}
          />
          <CustomRightArrow
            className={_classnames(classes.arrow, classes.rightArrow)}
            onClick={onRightClick}
          />
        </>
      )}
      <PauseButton active={!isPlaying} onClick={onPauseClick} />
    </Box>
  );
}

HomeHeroCarousel.propTypes = {
  children: PropTypes.node.isRequired,
};

const CustomArrowPropTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
};

CustomLeftArrow.propTypes = CustomArrowPropTypes;
CustomRightArrow.propTypes = CustomArrowPropTypes;

export default HomeHeroCarousel;
